import { useEffect, useRef, useState } from "react";

/**
 * 条件渲染的延迟卸载 hook —— 让"消失"也能播出场动画。
 *
 * 用法：
 *   const { mounted, exiting } = useDelayedUnmount(open, 150);
 *   if (!mounted) return null;
 *   return (
 *     <div className={exiting
 *       ? "animate-out fade-out duration-150"
 *       : "animate-in fade-in duration-150"}>
 *       ...
 *     </div>
 *   );
 *
 * @param {boolean} open   目标显示状态
 * @param {number} exitDuration 出场动画时长（ms），应与 exit 类的 duration-* 一致
 * @param {number} reducedExitDuration reduced-motion 下允许的纯透明度退出时长；默认 0 为立即卸载
 * @returns {{ mounted: boolean, exiting: boolean }}
 *   mounted=false 时不渲染；exiting=true 时挂出场类
 */
export function useDelayedUnmount(open, exitDuration = 150, reducedExitDuration = 0) {
  const [mounted, setMounted] = useState(open);
  const [exiting, setExiting] = useState(false);
  const mountedRef = useRef(open);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (open) {
      mountedRef.current = true;
      setMounted(true);
      setExiting(false);
      return;
    }
    if (!mountedRef.current) return; // 本来就没挂载，无需出场
    // prefers-reduced-motion：由调用方选择立即卸载，或保留极短的纯透明度退出。
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (exitDuration <= 0 || (reduced && reducedExitDuration <= 0)) {
      mountedRef.current = false;
      setMounted(false);
      setExiting(false);
      return;
    }
    setExiting(true);
    const actualExitDuration = reduced ? Math.min(exitDuration, reducedExitDuration) : exitDuration;
    timerRef.current = setTimeout(() => {
      mountedRef.current = false;
      setMounted(false);
      setExiting(false);
    }, actualExitDuration);
    return () => clearTimeout(timerRef.current);
  }, [open, exitDuration, reducedExitDuration]);

  return { mounted, exiting };
}

export default useDelayedUnmount;
